import React from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { PANEL_CONTENT_DEFAULT_SPACING } from './panel.constants';
import { useObjectMemo } from '@client/hooks/use-object.hooks';

type InsetPanelProps = {
  children: React.ReactNode;
  open?: boolean;
  hideBottomShadow?: boolean;
};

/**
 * Renders a full-width panel with inset shadows and animated expand/collapse.
 * @param props
 * @returns panel wrapper for nested content
 */
export const InsetPanel: React.FC<InsetPanelProps> = ({
  children,
  open = true,
  hideBottomShadow,
}) => {
  const { classes } = useStyles(useObjectMemo({ open, hideBottomShadow }));
  return (
    <Box className={classes.root}>
      <Box className={classes.content}>
        <Box className={classes.inner}>{children}</Box>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles<{ open: boolean; hideBottomShadow?: boolean }>()(
  (theme, { open, hideBottomShadow }) => ({
    root: {
      display: 'grid',
      gridTemplateRows: open ? '1fr' : '0fr',
      transition: 'grid-template-rows 200ms ease',
      width: `calc(100% + 2 * ${theme.spacing(PANEL_CONTENT_DEFAULT_SPACING)})`,
      marginLeft: theme.spacing(-PANEL_CONTENT_DEFAULT_SPACING),
      marginRight: theme.spacing(-PANEL_CONTENT_DEFAULT_SPACING),
    },
    content: {
      minHeight: 0,
      overflow: 'hidden',
    },
    inner: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      // Avoids layout measurements while animating open/close.
      boxShadow: `inset 0 6px 6px -6px ${theme.palette.action.disabled}, inset 0 -6px 6px -6px ${hideBottomShadow ? 'transparent' : theme.palette.action.disabled}}`,
    },
  }),
);
